/**
 * Builds the cofactor.md spec for the current flow once, so both the rendered
 * view (block model) and the canvas copy/download controls (full-fidelity
 * source) read from a single computation.
 *
 * @module FlowDesigner/spec/useFlowSpec
 */

import { useMemo } from 'react';
import type { SpecDocBlock } from '@chatsdet/canary-spec/browser';
import type { WorkflowEdge, WorkflowNode } from '../../../../types/workflow';
import { buildLiveWorkflow, type SpecWorkflowMeta } from './buildLiveWorkflow';
import { toBlocks, toFullFidelity, specFileName } from './renderCofactorMd';

export interface FlowSpec {
  /** Human-readable block model for the on-screen render. */
  blocks: SpecDocBlock[];
  /** Full-fidelity, re-compilable `.cofactor.md` for copy + download. */
  fullSource: string;
  /** Suggested download filename. */
  fileName: string;
}

export function useFlowSpec(args: {
  enabled: boolean;
  meta: SpecWorkflowMeta | null | undefined;
  name: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}): FlowSpec | null {
  const { enabled, meta, name, nodes, edges } = args;
  return useMemo(() => {
    if (!enabled || !meta) return null;
    const live = buildLiveWorkflow({ meta, name, nodes, edges });
    return {
      blocks: toBlocks(live),
      fullSource: toFullFidelity(live),
      fileName: specFileName(meta.name ?? name),
    };
  }, [enabled, meta, name, nodes, edges]);
}
